import ModalComponent from '../../../components/ModalComponent';
import zod from 'zod';
import { LicenseEditionFormSchema } from '../../../services/LicenseService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Grid2 } from '@mui/material';
import TextFieldComponent from '../../../components/TextFieldComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import DropdownSelectComponent from '../../../components/DropdownSelectComponent';
import DropdownComponent from '../../../components/DropdownComponent';
import { useContext, useState } from 'react';
import { useDoRegisterInternalAsync } from '../../../services/AuthService/hooks/useMutations';
import { AppContext } from '../../../context/AppContextProvider';

interface LicenseEditionProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleSaveForm?: () => void;
}

type LicenseFormInput = zod.infer<typeof LicenseEditionFormSchema>;

const LicenseEditionModal = ({
  isOpen,
  setIsOpen,
  handleSaveForm,
}: LicenseEditionProps) => {
  const { showSnackbar } = useContext(AppContext);
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<LicenseFormInput>({
    resolver: zodResolver(LicenseEditionFormSchema),
    defaultValues: {
      email: '',
      password: '',
      name: '',
      cpf: '',
      cnpj: '',
      isTrial: false,
      plans: [],
      usersAmount: 0,
      devicesAmount: 0,
      studentsAmount: 0,
      responsiblesAmount: 0,
      studentUnitValue: 0,
    },
  });
  const { mutateAsync, isLoading } = useDoRegisterInternalAsync();
  const [personType, setPersonType] = useState(0);

  const plansOptions = [
    { label: 'Education BASIC', value: 'EDUCATION_BASIC' },
    { label: 'Education STANDARD', value: 'EDUCATION_STANDARD' },
    { label: 'Education PREMIUM', value: 'EDUCATION_PREMIUM' },
    { label: 'Education DELUX', value: 'EDUCATION_DELUX' },
    { label: 'Security BASIC', value: 'SECURITY_BASIC' },
    { label: 'Security STANDARD', value: 'SECURITY_STANDARD' },
    { label: 'Security PREMIUM', value: 'SECURITY_PREMIUM' },
    { label: 'Security DELUX', value: 'SECURITY_DELUX' },
    { label: 'Traffic BASIC', value: 'TRAFFIC_BASIC' },
    { label: 'Traffic STANDARD', value: 'TRAFFIC_STANDARD' },
    { label: 'Traffic PREMIUM', value: 'TRAFFIC_PREMIUM' },
    { label: 'Traffic DELUX', value: 'TRAFFIC_DELUX' },
    { label: 'Retail BASIC', value: 'RETAIL_BASIC' },
    { label: 'Retail STANDARD', value: 'RETAIL_STANDARD' },
    { label: 'Retail PREMIUM', value: 'RETAIL_PREMIUM' },
    { label: 'Retail DELUX', value: 'RETAIL_DELUX' },
    { label: 'Sports BASIC', value: 'SPORTS_BASIC' },
    { label: 'Sports STANDARD', value: 'SPORTS_STANDARD' },
    { label: 'Sports PREMIUM', value: 'SPORTS_PREMIUM' },
    { label: 'Sports DELUX', value: 'SPORTS_DELUX' },
  ];
  const personTypeOptions = [
    { label: 'Pessoa física', value: 0 },
    { label: 'Pessoa Jurídica', value: 1 },
  ];

  const handleSave = (data: any) => {
    mutateAsync(data, {
      onSuccess: () => {
        showSnackbar('Licença cadastrada com sucesso.', 'success');
        setIsOpen(false);
        reset();
        if (handleSaveForm) handleSaveForm();
      },
    });
  };

  return (
    <ModalComponent
      title={'Edição de licença'}
      onClose={() => setIsOpen(false)}
      isOpen={isOpen}
    >
      <Grid2 container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Nome"
                errorMessage={error?.message}
                onChangeText={onChange}
                value={value}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Email"
                errorMessage={error?.message}
                onChangeText={onChange}
                value={value}
              />
            )}
            name={'email'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Senha"
                errorMessage={error?.message}
                onChangeText={onChange}
                value={value}
              />
            )}
            name={'password'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropdownSelectComponent
                label="Planos"
                options={plansOptions}
                labelField={'label'}
                valueField={'value'}
                errorMessage={error?.message}
                selectedValues={value}
                onSelectedValues={onChange}
              />
            )}
            name={'plans'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <DropdownComponent
            label={'Tipo de pessoa'}
            value={personType}
            optionLabel={'label'}
            optionValue={'value'}
            options={personTypeOptions}
            onChange={(val) => {
              if (val == 0) setValue('cnpj', '');
              else setValue('cpf', '');
              setPersonType(val);
            }}
          />
        </Grid2>

        <Grid2 size={6}>
          {personType === 0 ? (
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="CPF"
                  errorMessage={error?.message}
                  onChangeText={onChange}
                  value={value}
                />
              )}
              name={'cpf'}
              control={control}
            />
          ) : (
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="CNPJ"
                  errorMessage={error?.message}
                  onChangeText={onChange}
                  value={value}
                />
              )}
              name={'cnpj'}
              control={control}
            />
          )}
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Quantidade de usuários"
                errorMessage={error?.message}
                onChangeText={onChange}
                value={value}
              />
            )}
            name={'usersAmount'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Quantidade de dispositivos"
                errorMessage={error?.message}
                onChangeText={onChange}
                value={value}
              />
            )}
            name={'devicesAmount'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Quantidade de alunos"
                errorMessage={error?.message}
                onChangeText={onChange}
                value={value}
              />
            )}
            name={'studentsAmount'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Valor unitário por aluno"
                errorMessage={error?.message}
                onChangeText={onChange}
                value={value}
              />
            )}
            name={'studentUnitValue'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Quantidade de responsáveis"
                errorMessage={error?.message}
                onChangeText={onChange}
                value={value}
              />
            )}
            name={'responsiblesAmount'}
            control={control}
          />
        </Grid2>

        <Grid2 size={12}>
          <ButtonComponent
            fullWidth
            loading={isLoading}
            onClick={handleSubmit(handleSave)}
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </ModalComponent>
  );
};

export default LicenseEditionModal;
